// render-campaign-sessions.ts
//
// Client-side renderer for the campaign-sessions list (Upcoming + Past). Paints from the JSON island
// emitted by ft_campaign_sessions_mount() (includes/functions/janus/core/campaign_sessions_mount.php).
//
// Two modes, mirroring the two pages that used to hand-roll near-identical PHP:
//   'manage' — GM modify view: future rows get delete + reschedule controls (when can_manage).
//   'view'   — player read view: future rows get skip/rejoin controls when the user is a player.
//
// Both modes share one row shape (date tile · "Session N" · weekday/time/relative · actions); the
// styles live in css/src/99j-campaign-sessions.css. Skip/rejoin reuse the existing global handlers
// (queryData / hideEl / showEl); delete posts directly so the row can leave the list and the rest
// renumber in place. Events are delegated in TS instead of emitted as inline onclick strings.

export interface CsSession {
	id: string;
	start_time: string;      // UTC 'Y-m-d H:i:s' sort key (lexical sort == chronological)
	start_time_f: string;    // pre-formatted, already localized server-side
	month_f: string;         // 'Nov'        — date-tile parts, localized server-side
	day_f: string;           // '1'
	weekday_f: string;       // 'Sunday'
	time_f: string;          // '4:00 PM MST'
	year_f: string;          // '' when the session falls in the current year
	days_until: number;      // local calendar days from today (0 = today, negative = past)
	attending: number;
	in_campaign: number;
	user_is_player: boolean;
	user_is_skipping: boolean;
}

export interface CsMountData {
	mode: 'manage' | 'view';
	campaign_id: string;
	base_url: string;
	can_manage: boolean;
	counts: { total: number; future: number; past: number };
	future: CsSession[];     // ASC
	past: CsSession[];       // DESC
}

// Stash kept on the mount element so the add handler can repaint after mutating the data.
interface CsStash { data: CsMountData; }

const ICON_W = 20;
const ICON_H = 20;

function esc(s: unknown): string {
	return (typeof escapeHtml === 'function') ? escapeHtml(String(s ?? '')) : String(s ?? '');
}

// Render an icon as a plain <img>; accessible labels and tooltip metadata live on the controls.
function icon(key: string): string {
	return (typeof iconMap === 'function') ? iconMap(key, ICON_W, ICON_H, false) : '';
}

function sessionUrl(base: string, id: string): string {
	return `${base}tools/campaign_sessions/campaign_session.php?campaign_session_id=${id}`;
}

function tooltipAttrs(label: string): string {
	return `data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false"
		data-bs-original-title="${label}" aria-label="${label}"`;
}

// ---- shared row pieces ----------------------------------------------------------------------

function relativeDay(n: number): string {
	if (n === 0) return 'Today';
	if (n === 1) return 'Tomorrow';
	if (n === -1) return 'Yesterday';
	if (n > 1 && n < 14) return `In ${n} days`;
	if (n >= 14 && n < 60) return `In ${Math.round(n / 7)} weeks`;
	if (n < -1 && n > -14) return `${-n} days ago`;
	return '';
}

function dateTile(d: CsMountData, s: CsSession, num: number): string {
	return `
		<a class="ft-cs-date" href="${sessionUrl(d.base_url, s.id)}" aria-label="Open session ${num}">
			<span class="ft-cs-date-mon">${esc(s.month_f)}</span>
			<span class="ft-cs-date-day">${esc(s.day_f)}</span>
		</a>`;
}

function rowMain(d: CsMountData, s: CsSession, num: number, opts: { next?: boolean; attendance?: boolean }): string {
	const rel = relativeDay(s.days_until);
	const meta = [s.weekday_f, s.time_f, s.year_f].filter(Boolean).map(esc).join(' · ');
	const pills: string[] = [];
	if (opts.next) pills.push('<span class="ft-cs-pill ft-cs-pill--next">Next</span>');
	if (rel) pills.push(`<span class="ft-cs-pill">${esc(rel)}</span>`);
	if (opts.attendance && s.in_campaign > 0) {
		pills.push(`<span class="ft-cs-pill ft-cs-pill--quiet" title="Player characters attending">${s.attending}/${s.in_campaign} attending</span>`);
	}
	return `
		<div class="ft-cs-main">
			<div class="ft-cs-title-line">
				<a class="ft-cs-title" href="${sessionUrl(d.base_url, s.id)}">Session ${num}</a>
				${pills.join('')}
			</div>
			<div class="ft-cs-meta">${meta}</div>
		</div>`;
}

function row(d: CsMountData, s: CsSession, num: number, opts: { next?: boolean; attendance?: boolean; actions?: string; past?: boolean }): string {
	return `
		<li id="li_${s.id}" class="ft-cs-row${opts.past ? ' ft-cs-row--past' : ''}${opts.next ? ' ft-cs-row--next' : ''}">
			${dateTile(d, s, num)}
			${rowMain(d, s, num, opts)}
			${opts.actions ? `<div class="ft-cs-actions">${opts.actions}</div>` : ''}
			<div id="div_refresh_${s.id}" class="ft-cs-row-status"></div>
		</li>`;
}

// ---- manage mode (GM) -----------------------------------------------------------------------

function manageActions(d: CsMountData, s: CsSession): string {
	if (!d.can_manage) return '';
	return `
		<a href="${sessionUrl(d.base_url, s.id)}#reschedule_session_section" class="btn ft-cs-icon-btn"
			${tooltipAttrs('Reschedule Session')}
		>${icon('reschedule_session')}</a>
		<button id="btn_toggle_campaign_delete_${s.id}" type="button" class="btn ft-cs-icon-btn ft-cs-icon-btn--danger"
			${tooltipAttrs('Delete Session')}
			data-ft-cs-action="confirm-delete" data-ft-session-id="${s.id}"
		>${icon('delete_campaign_session')}</button>
		<button id="btn_toggle_campaign_delete_check_${s.id}" type="button" class="btn btn-sm btn-danger ft-cs-confirm" style="display: none;"
			aria-label="Confirm Delete Session"
			data-ft-cs-action="delete" data-ft-session-id="${s.id}" data-ft-base-url="${esc(d.base_url)}"
		>Delete?</button>`;
}

// ---- view mode (player) ---------------------------------------------------------------------

function viewSkipRejoin(d: CsMountData, s: CsSession): string {
	if (!s.user_is_player) return '';
	const skipDisplay = s.user_is_skipping ? 'none' : '';
	const rejoinDisplay = s.user_is_skipping ? '' : 'none';
	return `
		<button id="btn_skip_campaign_session_${s.id}" type="button" class="btn ft-cs-icon-btn" style="display: ${skipDisplay};"
			${tooltipAttrs('Skip Session')}
			data-ft-cs-action="skip" data-ft-session-id="${s.id}" data-ft-campaign-id="${esc(d.campaign_id)}" data-ft-base-url="${esc(d.base_url)}"
		>${icon('skip_campaign_session')}</button>
		<button id="btn_rejoin_campaign_session_${s.id}" type="button" class="btn ft-cs-icon-btn" style="display: ${rejoinDisplay};"
			${tooltipAttrs('Rejoin Session')}
			data-ft-cs-action="rejoin" data-ft-session-id="${s.id}" data-ft-campaign-id="${esc(d.campaign_id)}" data-ft-base-url="${esc(d.base_url)}"
		>${icon('rejoin_campaign_session')}</button>`;
}

// ---- list assembly --------------------------------------------------------------------------

function futureListHtml(d: CsMountData): string {
	if (!d.future.length) {
		return d.mode === 'manage'
			? '<p class="ft-cs-empty">No upcoming sessions — schedule one to get the table together.</p>'
			: '<p class="ft-cs-empty">No upcoming sessions yet.</p>';
	}
	const pastCount = d.counts.past;
	// Numbering is global-chronological: oldest past = 1 … newest future = total. Future starts right
	// after the past block, ascending. Recomputed every render so an inserted row renumbers cleanly.
	const rows = d.future.map((s, i) => row(d, s, pastCount + i + 1, {
		next: i === 0,
		attendance: true,
		actions: d.mode === 'manage' ? manageActions(d, s) : viewSkipRejoin(d, s),
	})).join('');
	return `<ol class="ft-cs-list">${rows}</ol>`;
}

function pastListHtml(d: CsMountData): string {
	if (!d.past.length) {
		return '<p class="ft-cs-empty">No past sessions yet.</p>';
	}
	const pastCount = d.counts.past;
	// Past is DESC: index 0 is the most recent past session (number = pastCount), down to 1.
	const rows = d.past.map((s, i) => row(d, s, pastCount - i, { past: true })).join('');
	return `<ol class="ft-cs-list">${rows}</ol>`;
}

function cardHead(title: string, count: number): string {
	return `
		<header class="ft-cs-card-head">
			<h2 class="ft-cs-card-title">${title}</h2>
			<span class="ft-cs-count">${count}</span>
		</header>`;
}

export function campaignSessionsHtml(d: CsMountData): string {
	return `
		<div class="ft-cs">
			<section class="ft-cs-card">
				${cardHead('Upcoming Sessions', d.future.length)}
				<div id="div_refresh_future_sessions">${futureListHtml(d)}</div>
			</section>
			<section class="ft-cs-card">
				${cardHead('Past Sessions', d.past.length)}
				${pastListHtml(d)}
			</section>
		</div>`;
}

// ---- mount + in-place update ----------------------------------------------------------------

function renderInto(el: HTMLElement, d: CsMountData): void {
	el.innerHTML = campaignSessionsHtml(d);
	(el as unknown as { _ftCsStash?: CsStash })._ftCsStash = { data: d };
	wireActions(el);
}

// The delete endpoint answers plain text ("Deleted" on success). On success the row leaves the stash
// and the list repaints, so the sessions after it renumber; anything else is shown on the row.
async function deleteSession(baseUrl: string, sessionId: string): Promise<void> {
	const confirmBtn = document.getElementById(`btn_toggle_campaign_delete_check_${sessionId}`) as HTMLButtonElement | null;
	const status = document.getElementById(`div_refresh_${sessionId}`);
	if (confirmBtn) confirmBtn.disabled = true;
	try {
		const res = await fetch(`${baseUrl}options/tools/campaign_sessions/delete_campaign_session_inc.php`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ campaign_session_id: sessionId }),
		});
		const text = await res.text();
		if (res.ok && /\bDeleted\b/.test(text)) {
			ftCampaignSessionsRemove(sessionId);
			return;
		}
		if (status) status.textContent = text.replace(/<[^>]*>/g, '').trim() || 'Could not delete the session.';
	} catch (e) {
		console.error('[campaign-sessions] delete failed', e);
		if (status) status.textContent = 'Something went wrong deleting the session. Please try again.';
	}
	if (confirmBtn) confirmBtn.disabled = false;
}

function wireActions(el: HTMLElement): void {
	if (el.getAttribute('data-ft-cs-actions') === '1') return;
	el.setAttribute('data-ft-cs-actions', '1');
	el.addEventListener('click', (event) => {
		const btn = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-ft-cs-action]');
		if (!btn || !el.contains(btn)) return;
		const action = btn.dataset.ftCsAction || '';
		const sessionId = btn.dataset.ftSessionId || '';
		const baseUrl = btn.dataset.ftBaseUrl || BASE_URL || '';
		if (action === 'confirm-delete') {
			hideEl(`btn_toggle_campaign_delete_${sessionId}`);
			showEl(`btn_toggle_campaign_delete_check_${sessionId}`);
			document.getElementById(`btn_toggle_campaign_delete_check_${sessionId}`)?.focus();
			return;
		}
		if (action === 'delete') {
			void deleteSession(baseUrl, sessionId);
			return;
		}
		if (action === 'skip' || action === 'rejoin') {
			const skipping = action === 'skip';
			if (skipping) {
				hideEl(`btn_skip_campaign_session_${sessionId}`);
				showEl(`btn_rejoin_campaign_session_${sessionId}`);
			} else {
				showEl(`btn_skip_campaign_session_${sessionId}`);
				hideEl(`btn_rejoin_campaign_session_${sessionId}`);
			}
			void queryData(
				`${baseUrl}options/tools/campaign_sessions_user_interactions/${skipping ? 'skip_campaign_session.php' : 'rejoin_campaign_session.php'}`,
				`div_refresh_${sessionId}`,
				{ campaign_id: btn.dataset.ftCampaignId || '', campaign_session_id: sessionId },
			);
		}
	});
}

function mountOne(el: Element): void {
	if (el.getAttribute('data-ft-cs-done') === '1') return;
	const blobEl = el.querySelector('script.ft-cs-blob');
	if (!blobEl) return;
	try {
		const data = JSON.parse(blobEl.textContent || 'null') as CsMountData | null;
		if (!data) return;
		el.setAttribute('data-ft-cs-done', '1');
		renderInto(el as HTMLElement, data);
	} catch (e) {
		console.error('[campaign-sessions] mount failed', e);
	}
}

export function ftMountCampaignSessions(root?: Element | Document): void {
	(root || document).querySelectorAll('[data-ft-campaign-sessions]:not([data-ft-cs-done="1"])').forEach(mountOne);
}

function liveStash(): { el: HTMLElement; d: CsMountData } | null {
	const el = document.querySelector('[data-ft-campaign-sessions]') as HTMLElement | null;
	const stash = el ? (el as unknown as { _ftCsStash?: CsStash })._ftCsStash : undefined;
	return (el && stash) ? { el, d: stash.data } : null;
}

// Insert a session row (from the add-session JSON response) into the live mount and repaint, without
// re-fetching the whole list. `bucket` says which list the new row belongs to.
export function ftCampaignSessionsInsert(session: CsSession, bucket: 'future' | 'past'): void {
	const live = liveStash();
	if (!live) { ftMountCampaignSessions(); return; }
	const { el, d } = live;

	// Drop any stale copy (idempotent on a double-submit), add, re-sort to the bucket's order.
	if (bucket === 'past') {
		d.past = d.past.filter(s => s.id !== session.id);
		d.past.push(session);
		d.past.sort((a, b) => (a.start_time < b.start_time ? 1 : a.start_time > b.start_time ? -1 : 0)); // DESC
		d.counts.past += 1;
	} else {
		d.future = d.future.filter(s => s.id !== session.id);
		d.future.push(session);
		d.future.sort((a, b) => (a.start_time < b.start_time ? -1 : a.start_time > b.start_time ? 1 : 0)); // ASC
		d.counts.future += 1;
	}
	d.counts.total += 1;
	renderInto(el, d);
}

// Drop a deleted session from the live mount and repaint (renumbers the sessions after it).
export function ftCampaignSessionsRemove(sessionId: string): void {
	const live = liveStash();
	if (!live) return;
	const { el, d } = live;
	if (d.future.some(s => s.id === sessionId)) {
		d.future = d.future.filter(s => s.id !== sessionId);
		d.counts.future = Math.max(0, d.counts.future - 1);
	} else if (d.past.some(s => s.id === sessionId)) {
		d.past = d.past.filter(s => s.id !== sessionId);
		d.counts.past = Math.max(0, d.counts.past - 1);
	} else {
		return;
	}
	d.counts.total = Math.max(0, d.counts.total - 1);
	renderInto(el, d);
}
